import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { compatApi } from '../../utils/Intercepter';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import GradeBadge from '../../components/GradeBadge';

// ─── Career duration calculator ───────────────────────────────────────────────
const calcMergedDays = (ranges) => {
    const valid = ranges
        .filter(r => r.start)
        .map(r => ({ s: dayjs(r.start).startOf('day').valueOf(), e: dayjs(r.end || dayjs()).startOf('day').valueOf() }))
        .filter(r => r.e >= r.s)
        .sort((a, b) => a.s - b.s);
    if (!valid.length) return 0;
    const merged = [{ ...valid[0] }];
    for (let i = 1; i < valid.length; i++) {
        const last = merged[merged.length - 1];
        if (valid[i].s <= last.e) last.e = Math.max(last.e, valid[i].e);
        else merged.push({ ...valid[i] });
    }
    return merged.reduce((sum, r) => sum + Math.round((r.e - r.s) / 86400000) + 1, 0);
};

const toCareerText = (days) => {
    if (days <= 0) return '0개월';
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / (365 / 12));
    const totalMonths = years * 12 + months;
    const yearStr = years > 0 ? `${years}년 ` : '';
    const monthStr = months > 0 ? `${months}개월` : (years === 0 ? '0개월' : '');
    return `${yearStr}${monthStr} (총 ${totalMonths}개월)`.trim();
};

// ─── Shared style helpers ─────────────────────────────────────────────────────
const cardS = {
    background: 'white', borderRadius: 16,
    border: '1px solid #f0eff5',
    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
    overflow: 'hidden',
};

const tblS = { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 };
const tdS = { padding: '14px 14px', borderBottom: '1px solid #f4f3f9', fontSize: 12.5, color: '#3a3849', verticalAlign: 'middle' };
const thS = { padding: '11px 14px', fontSize: 11.5, fontWeight: 600, color: '#5d5b6e', borderBottom: '1px solid #ececf2', whiteSpace: 'nowrap', letterSpacing: '.02em', background: '#f7f6fb', textAlign: 'center' };

function SHead({ title, count, badgeColor }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: 'linear-gradient(180deg,#faf9fd 0%,#f5f4fa 100%)', borderBottom: '1px solid #ececf2' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 4, height: 16, borderRadius: 2, background: `linear-gradient(180deg,${PURPLE},${MINT})`, flexShrink: 0, display: 'inline-block' }} />
                <span style={{ fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>{title}</span>
                {count != null && (
                    <span style={{ fontSize: 11, fontWeight: 600, background: hexToRgba(badgeColor || PURPLE, .12), color: badgeColor || PURPLE, padding: '2px 8px', borderRadius: 999, lineHeight: 1.45 }}>
                        {count}
                    </span>
                )}
            </div>
        </div>
    );
}

function EmptyRow({ colSpan, msg }) {
    return (
        <tr><td colSpan={colSpan}>
            <div style={{ padding: '44px 20px', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: hexToRgba(PURPLE, .08), color: PURPLE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                </div>
                <div>{msg}</div>
            </div>
        </td></tr>
    );
}

function InfoRow({ label, children }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>{label}</div>
            <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{children}</div>
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ReadUserInfo() {
    const currentUser = useLocation().state;
    const navigate = useNavigate();

    const [personalInfo, setPersonalInfo] = useState({
        userEmail: '', userName: '', userNameEn: '', organizationCode: '', organizationName: '',
        contact: '', emergencyContact: '', negotiationDate: '', negotiationState: '',
        negotiatedSalary: '', contactStartDate: '', contactEndDate: '',
        department: '', position: '', expiredDate: '', eduGrade: '', techGrade: '',
        careers: [], certificates: [], projects: [], preProjects: [],
        educations: [], itTrainings: [], specialSkills: [],
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await compatApi.post('/admin/user/userResumeInfo', {
                    userEmail: currentUser.userEmail,
                    organizationCode: currentUser.organizationCode,
                });
                setPersonalInfo(response.data);
            } catch (error) {
                console.log(error);
                alert('오류가 발생했습니다.');
            }
        };
        fetchData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDeleteUser = async () => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            const response = await compatApi.del('/admin/user/deleteUser', {
                userEmail: personalInfo.userEmail,
                organizationCode: personalInfo.organizationCode,
            });
            const message = typeof response.data === 'string' ? response.data : '';
            if (response.ok) {
                alert('삭제되었습니다.');
                navigate('/admin/readUserList');
            } else {
                alert(message || '삭제 중 오류가 발생했습니다.');
            }
        } catch (error) {
            console.error('사용자 삭제 오류:', error);
            alert('네트워크 오류가 발생했습니다.');
        }
    };

    const calculateFullCareer = () => {
        const careerList = personalInfo.careers || [];
        const isJoinDateMissing = !personalInfo.joinDate;
        const allRanges = [
            ...careerList.map(c => ({ start: c.startDate, end: c.endDate })),
            ...(isJoinDateMissing ? [] : [{ start: personalInfo.joinDate, end: null }]),
        ];
        const totalDays = calcMergedDays(allRanges);
        const prevOnlyDays = calcMergedDays(careerList.map(c => ({ start: c.startDate, end: c.endDate })));
        const currentOrgDays = isJoinDateMissing ? 0 : calcMergedDays([{ start: personalInfo.joinDate, end: null }]);
        const totalText = toCareerText(totalDays);
        const prevText = prevOnlyDays > 0 ? toCareerText(prevOnlyDays) : null;
        const currentText = isJoinDateMissing ? '입사일 없음' : toCareerText(currentOrgDays);
        return (
            <span style={{ fontSize: 13.5, color: '#1f1d2e' }}>
                <strong style={{ color: PURPLE }}>총 경력 {totalText}</strong>
                {prevText && <span style={{ color: '#5d5b6e' }}> / 이전 경력 {prevText}</span>}
                <span style={{ color: '#5d5b6e' }}> / 현재 </span>
                {isJoinDateMissing
                    ? <strong style={{ color: '#c44569' }}>{currentText}</strong>
                    : <span>{currentText}</span>
                }
            </span>
        );
    };

    const roleBadge = (role) => {
        const isMaster = role === 'MASTER';
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center',
                fontSize: 11, fontWeight: 700,
                color: isMaster ? PURPLE : '#5d5b6e',
                background: isMaster ? hexToRgba(PURPLE, .12) : '#f4f3f9',
                border: isMaster ? `1px solid ${hexToRgba(PURPLE, .20)}` : '1px solid #ececf2',
                padding: '3px 8px', borderRadius: 6, letterSpacing: '.06em',
            }}>{role || '-'}</span>
        );
    };

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <div style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

                {/* 페이지 헤더 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                            <span>관리자</span>
                            <span style={{ fontSize: 10 }}>›</span>
                            <span style={{ cursor: 'pointer', color: '#5d5b6e' }} onClick={() => navigate('/admin/readUserList')}>전체 회원 목록</span>
                            <span style={{ fontSize: 10 }}>›</span>
                            <span style={{ color: '#3a3849', fontWeight: 500 }}>회원 상세 정보</span>
                        </div>
                        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>회원 상세 정보</h1>
                        <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>
                            {personalInfo.userName || '—'} · {personalInfo.organizationName || '—'}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => navigate('/admin/readUserList')} style={{
                            height: 38, padding: '0 16px', borderRadius: 10,
                            background: 'white', border: '1.5px solid #ececf2',
                            color: '#3a3849', fontSize: 13, fontWeight: 500,
                            cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 5l-7 7 7 7" />
                            </svg>
                            목록으로
                        </button>
                        <button onClick={handleDeleteUser} style={{
                            height: 38, padding: '0 18px', borderRadius: 10, border: 'none',
                            background: 'rgba(196,69,105,.10)', color: '#c44569',
                            fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                            whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(196,69,105,.18)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(196,69,105,.10)'; }}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            </svg>
                            회원 삭제
                        </button>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* 기본 정보 */}
                    <div style={cardS}>
                        <SHead title="기본 정보" />
                        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 28, padding: '24px 24px 20px' }}>
                            {/* 아바타 */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 100, height: 100, borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'white', fontWeight: 700, fontSize: 32,
                                    boxShadow: `0 10px 24px -8px ${hexToRgba(PURPLE, .5)}`,
                                }}>
                                    {personalInfo.userName?.charAt(0) || '?'}
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#1f1d2e', marginBottom: 4 }}>{personalInfo.userName || '—'}</div>
                                    <div style={{ fontSize: 12, color: '#8b8a99', marginBottom: 6 }}>{personalInfo.organizationName || '—'}</div>
                                    {roleBadge(personalInfo.userRole)}
                                </div>
                            </div>
                            {/* 필드 */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 32 }}>
                                <InfoRow label="성명">{personalInfo.userName || '-'}</InfoRow>
                                <InfoRow label="영어 이름">{personalInfo.userNameEn || '-'}</InfoRow>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <InfoRow label="이메일"><span style={{ color: '#1f1d2e', wordBreak: 'break-all' }}>{personalInfo.userEmail || '-'}</span></InfoRow>
                                </div>
                                <InfoRow label="연락처">{personalInfo.contact || '-'}</InfoRow>
                                <InfoRow label="긴급연락처">{personalInfo.emergencyContact || '-'}</InfoRow>
                                <InfoRow label="소속 조직">{personalInfo.organizationName || '-'}</InfoRow>
                                <InfoRow label="권한">{roleBadge(personalInfo.userRole)}</InfoRow>
                            </div>
                        </div>
                    </div>

                    {/* 인사 정보 */}
                    <div style={cardS}>
                        <SHead title="인사 정보" />
                        <div style={{ padding: '20px 24px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 48 }}>
                                <InfoRow label="부서">{personalInfo.department || '-'}</InfoRow>
                                <InfoRow label="직책">{personalInfo.position || '-'}</InfoRow>
                                <InfoRow label="입사일">{personalInfo.joinDate || '-'}</InfoRow>
                                <InfoRow label="퇴사일">
                                    {personalInfo.expiredDate
                                        ? <span style={{ color: '#c44569', fontWeight: 600 }}>{personalInfo.expiredDate}</span>
                                        : <span style={{ color: '#c7c5d2' }}>—</span>
                                    }
                                </InfoRow>
                                <InfoRow label="연봉협상일">
                                    {personalInfo.negotiationDate ? dayjs(personalInfo.negotiationDate).format('MM월 DD일') : '-'}
                                </InfoRow>
                                {/* 등급 - 학력 기준 / 기술 기준, 한 줄에 함께 표시 */}
                                {/* 상위 2단 그리드(columnGap: 48)와 동일한 구조를 그대로 복제해야 "기술등급" 라벨이 다른 우측 항목과 같은 위치에서 시작한다 */}
                                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 48, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', gap: 12 }}>
                                        <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>학력등급</div>
                                        <div><GradeBadge grade={personalInfo.eduGrade} /></div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', gap: 12 }}>
                                        <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>기술등급</div>
                                        <div><GradeBadge grade={personalInfo.techGrade} /></div>
                                    </div>
                                </div>
                                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'start', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500, paddingTop: 4 }}>경력</div>
                                    <div>{calculateFullCareer()}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 프로젝트 */}
                    <div style={cardS}>
                        <SHead title="프로젝트" count={personalInfo.projects?.length || 0} />
                        <div style={{ overflowX: 'auto' }}>
                            <table style={tblS}>
                                <thead>
                                    <tr>
                                        <th style={thS}>프로젝트명</th>
                                        <th style={thS}>수행기간</th>
                                        <th style={thS}>고객사</th>
                                        <th style={thS}>역할</th>
                                        <th style={thS}>담당업무</th>
                                        <th style={thS}>기술스택</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {personalInfo.projects?.length > 0
                                        ? personalInfo.projects.map((p, i) => (
                                            <tr key={i} style={{ transition: 'background 80ms' }} onMouseEnter={(e) => e.currentTarget.style.background = '#fafaff'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                                                <td style={{ ...tdS, fontWeight: 500, color: '#1f1d2e' }}>{p.projectName || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center', color: '#5d5b6e', whiteSpace: 'nowrap' }}>{p.startDate} ~ {p.endDate || '현재'}</td>
                                                <td style={{ ...tdS, textAlign: 'center' }}>{p.client || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center' }}>{p.role || '-'}</td>
                                                <td style={tdS}>{p.assignments || '-'}</td>
                                                <td style={{ ...tdS, fontSize: 12 }}>{[p.os, p.dbInfo, p.language].filter(Boolean).join(' / ') || '-'}</td>
                                            </tr>
                                        ))
                                        : <EmptyRow colSpan={6} msg="프로젝트 정보가 없습니다." />
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* 이전 프로젝트 */}
                    <div style={cardS}>
                        <SHead title="이전 프로젝트" count={personalInfo.preProjects?.length || 0} />
                        <div style={{ overflowX: 'auto' }}>
                            <table style={tblS}>
                                <thead>
                                    <tr>
                                        <th style={thS}>프로젝트명</th>
                                        <th style={thS}>수행기간</th>
                                        <th style={thS}>고객사</th>
                                        <th style={thS}>근무처</th>
                                        <th style={thS}>역할</th>
                                        <th style={thS}>담당업무</th>
                                        <th style={thS}>기술스택</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {personalInfo.preProjects?.length > 0
                                        ? personalInfo.preProjects.map((p, i) => (
                                            <tr key={i} style={{ transition: 'background 80ms' }} onMouseEnter={(e) => e.currentTarget.style.background = '#fafaff'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                                                <td style={{ ...tdS, fontWeight: 500, color: '#1f1d2e' }}>{p.projectName || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center', color: '#5d5b6e', whiteSpace: 'nowrap' }}>{p.startDate} ~ {p.endDate || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center' }}>{p.client || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center' }}>{p.companyName || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center' }}>{p.role || '-'}</td>
                                                <td style={tdS}>{p.content || '-'}</td>
                                                <td style={{ ...tdS, fontSize: 12 }}>{[p.os, p.dbInfo, p.language].filter(Boolean).join(' / ') || '-'}</td>
                                            </tr>
                                        ))
                                        : <EmptyRow colSpan={7} msg="이전 프로젝트 정보가 없습니다." />
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* 자격증 */}
                    <div style={cardS}>
                        <SHead title="자격증" count={personalInfo.certificates?.length || 0} />
                        <div style={{ overflowX: 'auto' }}>
                            <table style={tblS}>
                                <thead>
                                    <tr>
                                        <th style={thS}>자격증 및 언어</th>
                                        <th style={thS}>취득일</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {personalInfo.certificates?.length > 0
                                        ? personalInfo.certificates.map((c, i) => (
                                            <tr key={i} style={{ transition: 'background 80ms' }} onMouseEnter={(e) => e.currentTarget.style.background = '#fafaff'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                                                <td style={{ ...tdS, fontWeight: 500, color: '#1f1d2e' }}>{c.certificateName || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center', color: '#5d5b6e' }}>
                                                    {c.acquisitionDate ? dayjs(c.acquisitionDate).format('YYYY년 MM월 DD일') : '-'}
                                                </td>
                                            </tr>
                                        ))
                                        : <EmptyRow colSpan={2} msg="자격증 정보가 없습니다." />
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* 경력 */}
                    <div style={cardS}>
                        <SHead title="경력" count={personalInfo.careers?.length || 0} />
                        <div style={{ overflowX: 'auto' }}>
                            <table style={tblS}>
                                <thead>
                                    <tr>
                                        <th style={thS}>근무처</th>
                                        <th style={thS}>기간</th>
                                        <th style={thS}>직위</th>
                                        <th style={thS}>업무내용</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {personalInfo.careers?.length > 0
                                        ? personalInfo.careers.map((c, i) => (
                                            <tr key={i} style={{ transition: 'background 80ms' }} onMouseEnter={(e) => e.currentTarget.style.background = '#fafaff'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                                                <td style={{ ...tdS, fontWeight: 500, color: '#1f1d2e' }}>{c.organizationName || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center', color: '#5d5b6e', whiteSpace: 'nowrap' }}>
                                                    {dayjs(c.startDate).format('YYYY년 MM월 DD일')} ~ {c.endDate ? dayjs(c.endDate).format('YYYY년 MM월 DD일') : <span style={{ color: MINT, fontWeight: 600 }}>재직중</span>}
                                                </td>
                                                <td style={{ ...tdS, textAlign: 'center' }}>{c.position || '-'}</td>
                                                <td style={tdS}>{c.task || '-'}</td>
                                            </tr>
                                        ))
                                        : <EmptyRow colSpan={4} msg="경력 정보가 없습니다." />
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* 학력 */}
                    <div style={cardS}>
                        <SHead title="학력" count={personalInfo.educations?.length || 0} />
                        <div style={{ overflowX: 'auto' }}>
                            <table style={tblS}>
                                <thead>
                                    <tr>
                                        <th style={thS}>학교명</th>
                                        <th style={thS}>기간</th>
                                        <th style={thS}>전공</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {personalInfo.educations?.length > 0
                                        ? personalInfo.educations.map((e, i) => (
                                            <tr key={i} style={{ transition: 'background 80ms' }} onMouseEnter={(ev) => ev.currentTarget.style.background = '#fafaff'} onMouseLeave={(ev) => ev.currentTarget.style.background = ''}>
                                                <td style={{ ...tdS, fontWeight: 500, color: '#1f1d2e' }}>{e.schoolName || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center', color: '#5d5b6e', whiteSpace: 'nowrap' }}>
                                                    {e.startDate ? dayjs(e.startDate).format('YYYY년 MM월') : '-'} ~ {e.endDate ? dayjs(e.endDate).format('YYYY년 MM월') : '-'}
                                                </td>
                                                <td style={{ ...tdS, textAlign: 'center' }}>{e.major || '-'}</td>
                                            </tr>
                                        ))
                                        : <EmptyRow colSpan={3} msg="학력 정보가 없습니다." />
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* 특수기술 */}
                    <div style={cardS}>
                        <SHead title="특수기술" count={personalInfo.specialSkills?.length || 0} />
                        <div style={{ overflowX: 'auto' }}>
                            <table style={tblS}>
                                <thead>
                                    <tr>
                                        <th style={thS}>특수기술명</th>
                                        <th style={thS}>숙련도</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {personalInfo.specialSkills?.length > 0
                                        ? personalInfo.specialSkills.map((s, i) => (
                                            <tr key={i} style={{ transition: 'background 80ms' }} onMouseEnter={(e) => e.currentTarget.style.background = '#fafaff'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                                                <td style={{ ...tdS, fontWeight: 500, color: '#1f1d2e' }}>{s.skillName || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center' }}>{s.proficiency || '-'}</td>
                                            </tr>
                                        ))
                                        : <EmptyRow colSpan={2} msg="특수기술 정보가 없습니다." />
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* IT 관련 교육 */}
                    <div style={cardS}>
                        <SHead title="IT 관련 교육" count={personalInfo.itTrainings?.length || 0} />
                        <div style={{ overflowX: 'auto' }}>
                            <table style={tblS}>
                                <thead>
                                    <tr>
                                        <th style={thS}>교육명</th>
                                        <th style={thS}>교육기관</th>
                                        <th style={thS}>기간</th>
                                        <th style={thS}>교육내용</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {personalInfo.itTrainings?.length > 0
                                        ? personalInfo.itTrainings.map((t, i) => (
                                            <tr key={i} style={{ transition: 'background 80ms' }} onMouseEnter={(e) => e.currentTarget.style.background = '#fafaff'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                                                <td style={{ ...tdS, fontWeight: 500, color: '#1f1d2e' }}>{t.title || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center' }}>{t.institution || '-'}</td>
                                                <td style={{ ...tdS, textAlign: 'center', color: '#5d5b6e', whiteSpace: 'nowrap' }}>
                                                    {t.startDate ? dayjs(t.startDate).format('YYYY-MM-DD') : '-'} ~ {t.endDate ? dayjs(t.endDate).format('YYYY-MM-DD') : <span style={{ color: MINT, fontWeight: 600 }}>진행 중</span>}
                                                </td>
                                                <td style={tdS}>{t.description || '-'}</td>
                                            </tr>
                                        ))
                                        : <EmptyRow colSpan={4} msg="IT 교육 정보가 없습니다." />
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
